import { useCallback, useMemo } from "react";
import { useCreate, useDelete, useTranslate, useUpdate } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import type {
  PlaybookResponse,
  PlaybookUpdateRequest,
} from "pages/media/types";
import type {
  AddonObject,
  AddonSettings,
} from "../context/PlaybookDraftContext";
import { mapDraftToPlaybookPayload, type PlaybookIdentity } from "../helpers";

/**
 * Create / rename / update / delete against the org's playbooks
 * (`media/{organizationId}/playbooks`), as the customize flow needs them.
 *
 * NOTE: this is the add-on-carrying resource the carousel reads, NOT
 * `saved-settings` — that one has no add-on fields at all (see
 * `mapDraftToPlaybookPayload`).
 *
 * Refine's useCreate/useUpdate/useDelete invalidate the resource's list
 * themselves (["list","many"] and, for update, "detail"), so nothing here
 * calls useInvalidate.
 *
 * Every call is silent except `deletePlaybook`: the rest is plumbing behind
 * something else the user asked for (an experience, an autosave with its own
 * indicator), so a toast would be noise. Failures surface through the returned
 * promise instead — `discardPlaybook` alone swallows them, see below.
 */
export const usePlaybookRecord = () => {
  const t = useTranslate();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;
  const resource = useMemo(
    () => (organizationId ? `media/${organizationId}/playbooks` : null),
    [organizationId]
  );

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update, isLoading: isUpdating } = useUpdate();
  const { mutateAsync: remove } = useDelete();

  /** Writes the draft as a new playbook and resolves its id. */
  const createPlaybook = useCallback(
    async (
      draft: AddonObject,
      identity: PlaybookIdentity,
      general?: AddonSettings
    ): Promise<string> => {
      if (!resource) throw new Error("No organization in session");
      const response = await create({
        resource,
        values: mapDraftToPlaybookPayload(draft, identity, general),
        successNotification: false,
        errorNotification: false,
      });
      return (response.data as PlaybookResponse).id;
    },
    [create, resource]
  );

  /** Names an existing playbook — the "keep it" path. */
  const renamePlaybook = useCallback(
    async (id: string, identity: PlaybookIdentity): Promise<void> => {
      if (!resource) throw new Error("No organization in session");
      await update({
        resource,
        id,
        values: {
          name: identity.name,
          ...(identity.description && { description: identity.description }),
        },
        successNotification: false,
        errorNotification: false,
      });
    },
    [update, resource]
  );

  /**
   * Writes back to an existing playbook: its add-on configuration from the
   * save bar in playbook-edit mode, or its name and description from the
   * carousel's Rename and the customize view's inline editor.
   *
   * Silent on purpose — the caller decides what to say. Every one of them
   * already reports for itself: the save bar and the inline editor toast their
   * own failure, and the rename modal stays open with the typed values. A
   * rejection is the cue for all of it.
   */
  const updatePlaybook = useCallback(
    async (id: string, values: PlaybookUpdateRequest): Promise<void> => {
      if (!resource) throw new Error("No organization in session");
      await update({
        resource,
        id,
        values,
        successNotification: false,
        errorNotification: false,
      });
    },
    [update, resource]
  );

  /**
   * Deletes a playbook the user explicitly asked to delete, and says so.
   *
   * The opposite of `discardPlaybook` below in both respects: this one is the
   * action the user came for, so it confirms, and its failure must reach them
   * rather than be swallowed.
   */
  const deletePlaybook = useCallback(
    async (id: string): Promise<void> => {
      if (!resource) throw new Error("No organization in session");
      await remove({
        resource,
        id,
        // Skip the default `resourceAll` invalidation so a per-record `useOne`
        // doesn't refetch the just-deleted id and 404 (same reason as MenuCTA).
        invalidates: ["list", "many"],
        successNotification: {
          type: "success",
          message: t("notifications.deleteSuccessGeneric"),
        },
        errorNotification: false,
      });
    },
    [remove, resource, t]
  );

  /**
   * Removes a playbook the user chose not to keep. Deliberately swallows its
   * failure: it runs while the just-created experience is being handed off, and
   * a failed cleanup must never strand the user on this page. Worst case is one
   * stray playbook, which the user can delete from the Playbooks list.
   */
  const discardPlaybook = useCallback(
    async (id: string): Promise<void> => {
      if (!resource) return;
      try {
        await remove({
          resource,
          id,
          successNotification: false,
          errorNotification: false,
        });
      } catch (error) {
        console.error("Error discarding playbook:", error);
      }
    },
    [remove, resource]
  );

  return {
    isUpdating,
    createPlaybook,
    renamePlaybook,
    updatePlaybook,
    deletePlaybook,
    discardPlaybook,
  };
};
